/**
 * KPI Card Component
 * Displays a single KPI with appropriate theme colors
 */

import { Dialog } from '@headlessui/react';
import React, { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../../Tw';
import { exportAsPngHtml2Canvas } from '../../../../utils/exportAsImage';
import { getBestContrastColor } from '../../../common/color.helper';
import { Button } from '../../../components/actions/Button';
import * as Icon from '../../../components/icons';
import { Stat } from '../../../components/stats/Stat';
import { StatListItem } from '../../../components/stats/StatListItem';
import Tag from '../../../helpContext/Tag';
import {
  humanizeIsoDuration,
  KPI_TAG_METADATA,
  KPIRelationship,
  KPIVariable,
  WarehouseKPI,
} from '../kpiModel';
import { getKpiStatusColor } from '../utilityFunctions';
import KpiBarChart from './KpiBarChart';
import KpiCardMenu, { MenuOption } from './KpiCardMenu';
import KpiFeatureToggles, { KpiFeatureToggleConfig } from './KpiFeatureToggles';
import RelatedKpiView from './RelatedKpiView';

interface KpiCardProps {
  className?: string;
  kpi: WarehouseKPI;
  value?: number;
  status?: 'critical' | 'warning' | 'good' | 'exceptional';
  trend?: 'up' | 'down' | 'stable';
  isLoading?: boolean;
  /**
   * Array of recent metric values for sparkline trend chart.
   * Example: [10, 12, 11, 13, 15, 14]
   */
  trendData?: number[];
  /** Related KPIs (other KPI cards) that should be displayed with this KPI */
  relatedKpis?: WarehouseKPI[];
  /** Current variable values if this KPI is parameterized */
  variableValues?: Record<string, any>;
  /** Called when a variable value is changed */
  onVariableChange?: (name: string, value: any) => void;
  /** Function to navigate to related KPI */
  onNavigateToRelated?: (kpiId: string) => void;
  /** Function to show visualization options */
  onShowVisualization?: (kpiId: string, visualizationType?: string) => void;
}

/**
 * KPI Card component for displaying a single KPI metric
 */
export const KpiCard: React.FC<KpiCardProps> = ({
  kpi,
  value,
  status = 'good',
  trend,
  className,
  isLoading = false,
  trendData,
  relatedKpis = [],
  variableValues = {},
  onVariableChange,
  onNavigateToRelated,
  onShowVisualization,
}) => {
  const { t } = useTranslation('app');

  const [showRelationships, setShowRelationships] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showRelatedKpiDetails, setShowRelatedKpiDetails] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [showChart, setShowChart] = useState(true);
  const [showTrend, setShowTrend] = useState(false);
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [showRelated, setShowRelated] = useState(true);
  const [showActionsSection, setShowActionsSection] = useState(true);
  const [showDataModal, setShowDataModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Get background and text colors based on status
  const bgColor = getKpiStatusColor(status);
  const textColor = getBestContrastColor(status);

  // Format the trend for display
  const trendDisplay = () => {
    if (!trend) return null;

    let icon;
    let color;

    if (trend === 'up') {
      icon = '↑';
      color = TwTheme.extend.colors.trend.up; // Green
    } else if (trend === 'down') {
      icon = '↓';
      color = TwTheme.extend.colors.trend.down; // Red
    } else {
      icon = '→';
      color = TwTheme.extend.colors.trend.stable; // Yellow
    }

    return (
      <span className="ml-2 inline-flex items-center" style={{ color }}>
        {icon}
      </span>
    );
  };

  // MOCK DATA for sparkline (for demo/development only)
  // Remove or replace with real data in production
  const mockTrendData = [10, 12, 11, 13, 15, 14, 16, 15, 17, 16];
  const effectiveTrendData =
    trendData && trendData.length > 0 ? trendData : mockTrendData;

  // Prepare sparkline data for nivo, using day labels for x axis
  // e.g., x: '1d', '2d', ...
  const benchmarkValue = kpi.benchmark?.standards?.site?.target;
  const sparkData =
    effectiveTrendData &&
    effectiveTrendData.length > 0 &&
    typeof benchmarkValue === 'number'
      ? [
          {
            id: 'metric',
            data: effectiveTrendData.map((y, i) => ({ x: `${i + 1}d`, y })),
          },
          {
            id: 'benchmark',
            data: effectiveTrendData.map((_, i) => ({
              x: `${i + 1}d`,
              y: benchmarkValue,
            })),
          },
        ]
      : undefined;

  const trendArray = effectiveTrendData;
  const avgValue =
    trendArray.length > 0
      ? trendArray.reduce((sum, v) => sum + v, 0) / trendArray.length
      : undefined;

  const percentDiff =
    trendArray.length > 1 && trendArray[0] !== 0
      ? ((trendArray[trendArray.length - 1] - trendArray[0]) /
          Math.abs(trendArray[0])) *
        100
      : undefined;

  // Suggest actions based on metric type or tags
  const getSuggestedActions = () => {
    // If KPI has relationships, check if any relationship has 'opposing' type
    if (kpi.relationships?.some(r => r.relationshipType === 'opposing')) {
      return ['Analyze Trade-offs', 'Optimize', 'Root Cause'];
    }

    // Example mapping by tag/metric type
    const tagSet = new Set(kpi.tags || []);
    if (tagSet.has('quality')) return ['Root Cause', 'Analyze'];
    if (tagSet.has('productivity')) return ['Optimize', 'Analyze'];
    if (tagSet.has('efficiency')) return ['Analyze', 'Optimize', 'Root Cause'];
    if (tagSet.has('cost')) return ['Analyze', 'Optimize'];
    if (tagSet.has('labor')) return ['Schedule', 'Analyze', 'Optimize'];
    // Default actions
    return ['Analyze', 'Optimize'];
  };

  const showActions =
    trend === 'down' || status === 'critical' || status === 'warning';

  const handleActionClick = (action: string) => {
    // Placeholder: replace with modal, navigation, etc.
    // eslint-disable-next-line no-console
    console.log(`Action triggered: ${action} for KPI ${kpi.id}`);
  };

  // Function to handle variable changes
  const handleVariableChange = (name: string, value: any) => {
    if (onVariableChange) {
      onVariableChange(name, value);
    }
  };

  // Function to find relationship info to display
  const getRelationshipInfo = (
    relationship: KPIRelationship,
  ): { label: string; description: string } => {
    const relationType = relationship.relationshipType;
    let label = '';

    switch (relationType) {
      case 'causes':
        label = 'Directly affects';
        break;
      case 'influenced_by':
        label = 'Influenced by';
        break;
      case 'correlated':
        label = 'Correlated with';
        break;
      case 'cascade':
        label = 'Cascade with';
        break;
      case 'opposing':
        label = 'Trade-off with';
        break;
      case 'supporting':
        label = 'Supports';
        break;
      default:
        label = 'Related to';
    }

    return {
      label,
      description:
        relationship.description ||
        `This KPI is ${label.toLowerCase()} the related KPI.`,
    };
  };

  // Function to render a variable input based on its type
  const renderVariableInput = (variable: KPIVariable) => {
    if (!variable.userConfigurable) return null;

    const currentValue = variableValues[variable.name] || variable.defaultValue;

    switch (variable.type) {
      case 'boolean':
        return (
          <div className="mt-1 flex items-center">
            <input
              type="checkbox"
              checked={currentValue}
              onChange={e =>
                handleVariableChange(variable.name, e.target.checked)
              }
              className="text-brand-600 focus:ring-brand-500 h-4 w-4 rounded border-gray-300"
            />
            <span className="ml-2 text-xs">{variable.label}</span>
          </div>
        );
      case 'enum':
        return (
          <div className="mt-1">
            <label className="mb-1 block text-xs">{variable.label}</label>
            <select
              value={currentValue}
              onChange={e =>
                handleVariableChange(variable.name, e.target.value)
              }
              className="bg-app-bg focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border-gray-300 px-2 py-1 text-xs shadow-sm"
            >
              {variable.enumValues?.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      case 'number':
        return (
          <div className="mt-1">
            <label className="mb-1 block text-xs">{variable.label}</label>
            <input
              type="range"
              min={variable.range?.min || 0}
              max={variable.range?.max || 100}
              step={variable.range?.step || 1}
              value={currentValue}
              onChange={e =>
                handleVariableChange(variable.name, parseFloat(e.target.value))
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs">
              <span>{variable.range?.min || 0}</span>
              <span>{currentValue}</span>
              <span>{variable.range?.max || 100}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Primary flag indicator (small badge if this is a primary KPI)
  const PrimaryBadge = () => {
    if (!kpi.isPrimary) return null;

    return (
      <span className="bg-brand-600 ml-1 rounded px-1 py-0.5 text-xs text-white">
        {t`Primary`}
      </span>
    );
  };

  // Visualization controls based on recommendedVisualization
  const VisualizationControls = () => {
    if (!kpi.recommendedVisualization) return null;

    return (
      <div data-component="VisualizationControls" className="mt-3">
        <p className="mb-1 text-xs font-medium">Visualization options:</p>
        <div className="flex flex-wrap gap-1">
          <Button
            label={kpi.recommendedVisualization.primaryType}
            buttonType="secondary"
            buttonSize="xs"
            className="rounded !bg-opacity-20 hover:!bg-opacity-100"
            onPress={() =>
              onShowVisualization?.(
                kpi.id,
                kpi.recommendedVisualization?.primaryType,
              )
            }
          />
          {kpi.recommendedVisualization.alternativeTypes?.map(vizType => (
            <Button
              key={vizType}
              label={vizType}
              buttonType="secondary"
              buttonSize="xs"
              className="rounded !bg-opacity-20 hover:!bg-opacity-100"
              onPress={() => onShowVisualization?.(kpi.id, vizType)}
            />
          ))}
        </div>
      </div>
    );
  };

  // Export KPI data as CSV
  const exportAsCSV = () => {
    const { t } = useTranslation('app');
    if (!trendArray || trendArray.length === 0) return;

    // Create CSV header
    const headers = ['Date', kpi.title];

    // Create CSV rows
    const rows = trendArray.map((value, index) => {
      // Create a mock date (28 days ago to today)
      const date = new Date();
      date.setDate(date.getDate() - (trendArray.length - 1 - index));
      return [date.toISOString().split('T')[0], value];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `WareBee-${kpi.id}-data-${new Date().toISOString().split('T')[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export KPI card as PNG
  const exportAsPNG = async (method: 'html2canvas') => {
    if (!cardRef.current) return;
    setIsExporting(true);
    try {
      if (method === 'html2canvas') {
        await exportAsPngHtml2Canvas(
          cardRef.current,
          `WareBee-${kpi.id}-card-${new Date().toISOString().split('T')[0]}.png`,
        );
      }
    } finally {
      setIsExporting(false);
    }
  };

  // Toggle flip-card effect
  const toggleFlipCard = () => {
    setIsFlipped(!isFlipped);
  };

  // Create menu options
  const menuOptions: MenuOption[] = [
    {
      id: 'export-csv',
      label: t`Export as CSV`,
      icon: <Icon.Download className="h-5 w-5 fill-current" />,
      onClick: exportAsCSV,
    },
    {
      id: 'export-png-html2canvas',
      label: isExporting ? t`Exporting...` : t`Export PNG`,
      icon: <Icon.CloudDownload className="h-5 w-5 fill-current" />,
      onClick: () => exportAsPNG('html2canvas'),
    },

    {
      id: 'show-data',
      label: t`Show Data`,
      icon: <Icon.ShowTable className="h-5 w-5 fill-current" />,
      onClick: () => setShowDataModal(true),
    },
  ];

  // Add configure variables option if variables exist
  if (kpi.variables && kpi.variables.length > 0) {
    menuOptions.push({
      id: 'configure-variables',
      label: t`Configure`,
      icon: <Icon.Settings className="h-5 w-5 fill-current" />,
      onClick: toggleFlipCard,
    });
  }

  const filteredRelatedKpis = kpi.relationships
    ? kpi.relationships
        .map(rel => relatedKpis.find(k => k.id === rel.relatedKpiId))
        .filter(Boolean)
    : [];

  // Modular toggles config for KpiFeatureToggles
  const featureToggles: KpiFeatureToggleConfig[] = [
    { key: 'chart', label: t`Chart`, value: showChart, setValue: setShowChart },
    { key: 'trend', label: t`Trend`, value: showTrend, setValue: setShowTrend },
    {
      key: 'benchmark',
      label: t`Benchmark`,
      value: showBenchmark,
      setValue: setShowBenchmark,
    },
    {
      key: 'related',
      label: t`Related KPIs`,
      value: showRelated,
      setValue: setShowRelated,
    },
    {
      key: 'actions',
      label: t`Recommended Actions`,
      value: showActionsSection,
      setValue: setShowActionsSection,
    },
  ];

  return (
    <div
      data-component="KpiCard"
      ref={cardRef}
      className={`relative ${className}`}
    >
      {/* Show Data Modal (dummy) */}
      <Dialog
        open={showDataModal}
        onClose={() => setShowDataModal(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          <div className="relative z-10 w-full max-w-lg rounded bg-white p-6">
            <Dialog.Title className="mb-2 text-lg font-bold">
              KPI Data (Dummy)
            </Dialog.Title>
            <div className="mb-4 text-sm text-gray-700">
              This is a placeholder for the Show Data feature.
            </div>
            <Button
              label="Close"
              buttonType="primary"
              onPress={() => setShowDataModal(false)}
            />
          </div>
        </div>
      </Dialog>

      {/* Flip card container */}
      <div
        className={`flip-container ${isFlipped ? 'flipped' : ''}`}
        style={{
          perspective: '1000px',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.6s',
          transform: isFlipped ? 'rotateY(180deg)' : '',
        }}
      >
        {/* Front side - Regular KPI card */}
        <div
          data-component="KpiCardFront"
          className="front"
          style={{
            position: isFlipped ? 'absolute' : 'relative',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            visibility: isFlipped ? 'hidden' : 'visible',
          }}
        >
          <Stat
            titleElement={
              <div className="flex w-full items-center justify-between">
                <div className="flex flex-1 items-center">
                  {kpi.title}
                  <PrimaryBadge />
                </div>
                <div className="flex items-center">
                  <div className="mr-2 text-2xl">{trendDisplay()}</div>
                  <KpiCardMenu options={menuOptions} />
                </div>
              </div>
            }
            titleAlt={kpi.category}
            className="!bg-app-panel-stat"
            inFilterStat
            value={value}
            unitOfMeasure={kpi.unit}
          >
            {isLoading ? (
              <div className="mt-2 animate-pulse space-y-2">
                <div
                  className="h-10 rounded"
                  style={{ backgroundColor: TwTheme.extend.colors.app.menu }}
                ></div>
                <div
                  className="h-4 w-1/2 rounded"
                  style={{ backgroundColor: TwTheme.extend.colors.app.menu }}
                ></div>
              </div>
            ) : (
              <>
                {showChart && (
                  <KpiBarChart
                    data={effectiveTrendData}
                    // thresholds={
                    //   showBenchmark ? kpi.benchmark?.standards?.site : {}
                    // }
                    palette={kpi.recommendedVisualization?.palette}
                  />
                )}
                {showTrend && kpi.trendWindow && (
                  <div className="mt-1">
                    <StatListItem
                      legendType="line"
                      legendColor={TwTheme.extend.colors.trend.down}
                      title={t('{{trend}} Trend Avg.', {
                        trend: humanizeIsoDuration(kpi.trendWindow),
                      })}
                      value={avgValue !== undefined ? avgValue : '—'}
                      unitOfMeasure={kpi.unit}
                    />
                    <StatListItem
                      title={t`% Change`}
                      value={
                        percentDiff !== undefined
                          ? `${percentDiff > 0 ? '+' : ''}${percentDiff.toFixed(1)}`
                          : '—'
                      }
                      unitOfMeasure={`%`}
                    />
                  </div>
                )}
                {/* Relationships section */}
                {showRelated &&
                  kpi.relationships &&
                  kpi.relationships.length > 0 && (
                    <div className="mt-3 pt-2">
                      <button
                        className="flex w-full items-center justify-between text-xs font-medium"
                        onClick={() => setShowRelationships(!showRelationships)}
                      >
                        <span>{t`Related KPIs`}</span>
                        <span>{showRelationships ? '▲' : '▼'}</span>
                      </button>
                      {showRelationships && (
                        <div className="mt-2">
                          {kpi.relationships.map((relationship, index) => {
                            const { label, description } =
                              getRelationshipInfo(relationship);
                            const strength = relationship.strength
                              ? `(${Math.round(relationship.strength * 100)}%)`
                              : '';
                            const relatedKpi = relatedKpis.find(
                              k => k.id === relationship.relatedKpiId,
                            );
                            return (
                              <div
                                data-component="related-kpi"
                                key={`${relationship.relatedKpiId}-${index}`}
                                className="bg-menu mb-4 rounded p-2 text-xs shadow-xl"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">
                                    {label} {strength}
                                  </span>
                                  {onNavigateToRelated && (
                                    <Button
                                      label="View"
                                      buttonType="secondary"
                                      buttonSize="xs"
                                      className="rounded !bg-opacity-20 hover:!bg-opacity-100"
                                      onPress={() =>
                                        onNavigateToRelated(
                                          relationship.relatedKpiId,
                                        )
                                      }
                                    />
                                  )}
                                </div>
                                <p className="text-menu-text/50 mt-0.5 text-xs">
                                  {description}
                                </p>
                                {relationship.lag && (
                                  <p className="text-menu-text/50 text-xs">
                                    {t('Lag: {{lag}}', {
                                      lag: humanizeIsoDuration(
                                        relationship.lag,
                                      ),
                                    })}
                                  </p>
                                )}
                                <div className="bg-menu mt-2">
                                  <div className="text-menu-text/80 text-xs font-medium">
                                    {relatedKpi?.title} {relatedKpi?.unit}
                                  </div>
                                  <div className="">
                                    <KpiBarChart
                                      data={effectiveTrendData}
                                      // thresholds={
                                      //   relatedKpi?.benchmark?.standards?.site
                                      // }
                                      showTrendLine
                                      unit={relatedKpi?.unit}
                                      mini
                                      sequentialPaletteId={
                                        relatedKpi?.recommendedVisualization
                                          ?.sequentialPaletteId
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {kpi.relationships &&
                            kpi.relationships.length > 0 &&
                            relatedKpis.length > 0 && (
                              <div className="mt-2 text-center">
                                <Button
                                  label={
                                    showRelatedKpiDetails
                                      ? 'Hide Full Charts'
                                      : 'View All Related Charts'
                                  }
                                  buttonType="secondary"
                                  buttonSize="sm"
                                  className="w-full rounded"
                                  onPress={() =>
                                    setShowRelatedKpiDetails(
                                      !showRelatedKpiDetails,
                                    )
                                  }
                                />
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                {/* Recommended Actions section */}
                {showActionsSection &&
                  // kpi.recommendedActions &&
                  kpi?.recommendedActions?.length > 0 && (
                    <div className="mt-3 pt-2">
                      <button
                        className="flex w-full items-center justify-between text-xs font-medium"
                        onClick={() =>
                          setShowActionsSection(!showActionsSection)
                        }
                      >
                        <span>{t`Recommended Actions`}</span>
                        <span>{showActionsSection ? '▲' : '▼'}</span>
                      </button>
                      {showActionsSection && (
                        <div className="">
                          {kpi.recommendedActions.map((action, idx) => (
                            <div
                              key={idx}
                              className="bg-menu mb-4 rounded p-2 text-xs shadow-xl"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">
                                  {action.recommendedAction}
                                  {action.priority && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      [{action.priority}]
                                    </span>
                                  )}
                                </span>
                              </div>
                              {action.tools && action.tools.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-2">
                                  {action.tools.map(tool => (
                                    <Button
                                      key={tool.id}
                                      label={tool.name}
                                      buttonType="secondary"
                                      buttonSize="xs"
                                      className="rounded !bg-opacity-20 hover:!bg-opacity-100"
                                      onPress={() =>
                                        tool.link &&
                                        window.open(tool.link, '_blank')
                                      }
                                    />
                                  ))}
                                </div>
                              )}
                              {action.steps && action.steps.length > 0 && (
                                <ul className="mt-1 list-disc pl-4 text-xs text-gray-400">
                                  {action.steps.map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
              </>
            )}
            <div data-component="KpiCardFrontContent" className="">
              {/* <Callout className="hidden" panelMode type="suggestion">
                {kpi.description}
              </Callout> */}

              {/* Suggested Actions */}
              {/* {showActions && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {getSuggestedActions().map(action => (
                    <Button
                      key={action}
                      label={action}
                      buttonType="secondary"
                      buttonSize="xs"
                      className="rounded !bg-opacity-20 hover:!bg-opacity-100"
                      onPress={() => handleActionClick(action)}
                    />
                  ))}
                </div>
              )} */}

              {/* Recommended visualization options */}
              {kpi.recommendedVisualization && onShowVisualization && (
                <VisualizationControls />
              )}
            </div>
            <div className="hidden">
              {kpi.tags?.map((tag, i) => {
                const meta = KPI_TAG_METADATA[tag];
                return (
                  <Tag
                    key={`${kpi.id}-tag-${i}`}
                    className="mr-2 inline-block rounded-full px-2 py-0.5"
                    title={meta?.title || tag}
                  >
                    {meta?.title || tag}
                  </Tag>
                );
              })}
            </div>
          </Stat>
        </div>

        {/* Back side - Variables Configuration */}
        <div
          data-component="KpiCardBack"
          className="back"
          style={{
            position: isFlipped ? 'relative' : 'absolute',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            visibility: isFlipped ? 'visible' : 'hidden',
          }}
        >
          <div className="bg-app-panel-stat p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-medium">
                {kpi.title} Configuration
              </h3>
              <button
                onClick={toggleFlipCard}
                className="hover:bg-menu rounded-full p-1 focus:outline-none"
              >
                <Icon.Refresh className="h-5 w-5 fill-current" />
              </button>
            </div>

            {kpi.variables && kpi.variables.length > 0 ? (
              <div className="space-y-3">
                {kpi.variables
                  .filter(v => v.userConfigurable)
                  .map(variable => (
                    <div key={variable.name} className="mb-3">
                      <div className="mb-1">
                        <span className="text-sm font-medium">
                          {variable.label}
                        </span>
                        {variable.description && (
                          <p className="text-xs text-gray-400">
                            {variable.description}
                          </p>
                        )}
                      </div>
                      {renderVariableInput(variable)}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="py-4 text-center text-gray-400">
                <p>{t`No configurable variables available for this KPI.`}</p>
              </div>
            )}

            {/* Feature Toggles Section */}
            <KpiFeatureToggles toggles={featureToggles} />

            <div className="mt-6 border-t border-gray-700 pt-4">
              <Button
                label="Apply Changes"
                buttonType="primary"
                buttonSize="sm"
                className="w-full"
                onPress={toggleFlipCard}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Related KPI View */}
      {showRelatedKpiDetails && (
        <div className="mt-4 border-t border-gray-700 pt-4">
          <RelatedKpiView
            primaryKpi={kpi}
            relatedKpis={filteredRelatedKpis}
            onNavigateToKpi={onNavigateToRelated}
            onShowVisualization={onShowVisualization}
          />
        </div>
      )}
    </div>
  );
};

export default KpiCard;
